import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EntityListLayout, FilterPanel, BulkWineFilters, Pagination } from '../../components'
import type { BulkWineFilterValues, PageSizeOption } from '../../components'
import { Card } from '../../components/ui/Card'
import { ActionMenu } from '../../components/ui/ActionMenu'
import { Table, TableHeader, TableBody, TableRow, TableHeaderCell, TableCell } from '../../components/ui/Table'
import { ExportOverlay, LoadingOverlay } from '../../components/ExportOverlay'
import { EmailExportCheckbox } from '../../components/EmailExportCheckbox'
import { SendEmailModal } from '../../components/SendEmailModal'
import { useBulkWines, useDeleteBulkWine } from '../../hooks/useBulkWines'
import { useEmailExport } from '../../hooks/useEmailExport'
import { useAuth } from '../../hooks/useAuth'
import { bulkWinesService } from '../../services/bulk-wines.service'
import { triggerBlobDownload } from '../../utils/download'
import { formatVolume } from '../../utils/formatters'
import { generateBulkWineEmailContent } from '../../utils/emailContentGenerator'
import { buildBulkWineSpecSheetFilename, buildBulkWineZipFilename, USER_ROLES } from '@ministry-of-wine/shared'
import { Download, Trash2, Pencil, Eye, ChevronsUpDown, ChevronUp, ChevronDown } from 'lucide-react'

interface BulkWine {
	id: number
	code: string
	variety: string
	vintage: string
	volume: number
	countryRegion: string
	// Pre-formatted "<sub-region>, <region>" of where the wine is stored, or 'N/A'.
	location: string
	composition: string
	pricePerLiter: number
	priceMargin?: number | null
	priceMarginUnit?: string | null
	priceCurrency: string
	supplierName: string
}

interface SelectionCheckboxProps {
	checked: boolean
	onChange: () => void
	ariaLabel: string
}

const getErrorMessage = (error: unknown, fallback: string) => {
	if (
		typeof error === 'object' &&
		error !== null &&
		'message' in error &&
		typeof (error as { message?: unknown }).message === 'string'
	) {
		return (error as { message: string }).message
	}

	return fallback
}

const SelectionCheckbox = ({ checked, onChange, ariaLabel }: SelectionCheckboxProps) => (
	<input
		type="checkbox"
		checked={checked}
		onChange={onChange}
		aria-label={ariaLabel}
		className="h-4 w-4 cursor-pointer appearance-none border border-[#7CB376] bg-white bg-center bg-no-repeat"
		style={{
			backgroundColor: checked ? '#7CB376' : '#FFFFFF',
			borderColor: '#7CB376',
			backgroundImage: checked
				? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='8' viewBox='0 0 10 8' fill='none'%3E%3Cpath d='M1 4L3.5 6.5L9 1' stroke='white' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
				: 'none',
			backgroundSize: '10px 8px',
			// Defensive sizing to prevent browser extension CSS conflicts
			width: '16px',
			minWidth: '16px',
			maxWidth: '16px',
			height: '16px',
			minHeight: '16px',
			maxHeight: '16px',
			boxSizing: 'border-box',
			flexShrink: 0,
		}}
	/>
)

export function BulkWinePage() {
	const navigate = useNavigate()
	const { data: currentUser } = useAuth()
	// PDF export (download/zip/email) is superadmin-only.
	const isSuperadmin = currentUser?.role === USER_ROLES.SUPERADMIN
	const [isFilterOpen, setIsFilterOpen] = useState(false)
	const [activeFilters, setActiveFilters] = useState<BulkWineFilterValues | null>(null)
	const [currentFilters, setCurrentFilters] = useState<BulkWineFilterValues | null>(null)
	const [getFiltersRef, setGetFiltersRef] = useState<(() => BulkWineFilterValues) | null>(null)
	const [currentPage, setCurrentPage] = useState(1)
	const [itemsPerPage, setItemsPerPage] = useState<PageSizeOption>(10)
	const [sortField, setSortField] = useState<keyof BulkWine>('code')
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
	const [selectedWineIds, setSelectedWineIds] = useState<number[]>([])
	const [isExporting, setIsExporting] = useState(false)
	const [isExportSuccess, setIsExportSuccess] = useState(false)
	const [emailMode, setEmailMode] = useState(true)

	const emailExport = useEmailExport()
	const queryParams = useMemo(() => {
		const params = new URLSearchParams({
			page: currentPage.toString(),
			limit: itemsPerPage === 'all' ? '1000000' : itemsPerPage.toString(),
		})
		if (activeFilters) {
			if (activeFilters.code) params.append('code', activeFilters.code)
			if (activeFilters.supplierId) params.append('supplierId', activeFilters.supplierId)
			if (activeFilters.vintage) params.append('vintage', activeFilters.vintage)
			if (activeFilters.volume) params.append('volume', activeFilters.volume)
			if (activeFilters.varietyIds && activeFilters.varietyIds.length > 0) {
				params.append('varietyIds', activeFilters.varietyIds.join(','))
			}
			if (activeFilters.countryId) params.append('countryId', activeFilters.countryId)
			if (activeFilters.regionId) params.append('regionId', activeFilters.regionId)
			if (activeFilters.subRegionId) params.append('subRegionId', activeFilters.subRegionId)
			if (activeFilters.locationRegionId) params.append('locationRegionId', activeFilters.locationRegionId)
			if (activeFilters.locationSubRegionId) {
				params.append('locationSubRegionId', activeFilters.locationSubRegionId)
			}
		}
		return params
	}, [activeFilters, currentPage, itemsPerPage])

	const bulkWinesQuery = useBulkWines(queryParams)
	const deleteBulkWine = useDeleteBulkWine()

	// Extract data with safe defaults
	const bulkWines = (bulkWinesQuery.data?.data || []) as BulkWine[]
	const totalEntries = bulkWinesQuery.data?.pagination?.total || 0
	const totalPages = bulkWinesQuery.data?.pagination?.totalPages || 1
	const { isFetching, renderState } = bulkWinesQuery

	const handleSort = (field: keyof BulkWine) => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
		} else {
			setSortField(field)
			setSortDirection('asc')
		}
	}

	const SortIcon: React.FC<{ field: keyof BulkWine }> = ({ field }) => {
		if (sortField !== field) {
			return <ChevronsUpDown size={16} color="#D0D5DD" style={{ flexShrink: 0, minWidth: '16px' }} />
		}

		return sortDirection === 'asc' ? (
			<ChevronUp size={16} color="#404040" style={{ flexShrink: 0, minWidth: '16px' }} />
		) : (
			<ChevronDown size={16} color="#404040" style={{ flexShrink: 0, minWidth: '16px' }} />
		)
	}

	const sortedBulkWines = [...bulkWines].sort((a, b) => {
		const aVal = a[sortField]
		const bVal = b[sortField]
		const modifier = sortDirection === 'asc' ? 1 : -1

		if (aVal === null || aVal === undefined) return 1
		if (bVal === null || bVal === undefined) return -1

		if (typeof aVal === 'string' && typeof bVal === 'string') {
			return aVal.localeCompare(bVal) * modifier
		}

		return (aVal > bVal ? 1 : -1) * modifier
	})

	const columnWidths = {
		select: '4%',
		code: '8%',
		variety: '9%',
		vintage: '7%',
		volume: '8%',
		countryRegion: '12%',
		location: '10%',
		composition: '13%',
		price: '9%',
		supplier: '10%',
		actions: '10%',
	} as const

	const visibleWineIds = sortedBulkWines.map((wine) => wine.id)
	const selectedVisibleCount = visibleWineIds.filter((id) => selectedWineIds.includes(id)).length
	const allVisibleSelected = visibleWineIds.length > 0 && selectedVisibleCount === visibleWineIds.length

	const handleDeleteBulkWine = async (wine: BulkWine) => {
		if (!window.confirm(`Delete bulk wine "${wine.code}"?`)) {
			return
		}

		try {
			await deleteBulkWine.mutateAsync(wine.id)
		} catch (error) {
			alert(getErrorMessage(error, 'Failed to delete bulk wine. Please try again.'))
		}
	}

	const handleToggleWineSelection = (wineId: number) => {
		setSelectedWineIds((currentIds) =>
			currentIds.includes(wineId) ? currentIds.filter((id) => id !== wineId) : [...currentIds, wineId],
		)
	}

	const handleToggleAllVisibleWines = () => {
		setSelectedWineIds((currentIds) => {
			if (allVisibleSelected) {
				return currentIds.filter((id) => !visibleWineIds.includes(id))
			}

			return Array.from(new Set([...currentIds, ...visibleWineIds]))
		})
	}

	const selectedCount = selectedWineIds.length
	const handlePageSizeChange = (pageSize: PageSizeOption) => {
		setItemsPerPage(pageSize)
		setCurrentPage(1)
	}
	const handleFiltersReady = useCallback((getFilters: () => BulkWineFilterValues) => {
		setGetFiltersRef(() => getFilters)
	}, [])

	const handleExportSelected = async () => {
		if (selectedCount === 0 || isExporting) {
			return
		}

		// Get selected wines data for email content
		const selectedWines = bulkWines.filter((wine) => selectedWineIds.includes(wine.id))

		if (emailMode) {
			// Email mode: open modal with pre-populated content
			try {
				const emailContent = generateBulkWineEmailContent(
					selectedWines,
					currentUser
						? {
								name: currentUser.name,
								email: currentUser.email,
							}
						: undefined,
				)

				// Build filename using shared function
				let filename: string
				if (selectedWines.length > 1) {
					filename = buildBulkWineZipFilename()
				} else {
					const wine = selectedWines[0]

					filename = buildBulkWineSpecSheetFilename({
						code: wine.code,
					})
				}

				emailExport.openModal({
					subject: emailContent.subject,
					body: emailContent.body,
					attachmentNames: [filename],
					sendFn: async (formData) => {
						await bulkWinesService.emailMultiple({
							...formData,
							ids: selectedWineIds,
						})
					},
				})
			} catch (error) {
				alert(getErrorMessage(error, 'Failed to prepare email. Please try again.'))
			}
		} else {
			// Download mode: existing behavior
			setIsExporting(true)
			setIsExportSuccess(false)
			try {
				const { blob, filename } = await bulkWinesService.downloadPdfZip(selectedWineIds)
				triggerBlobDownload(blob, filename)
				setIsExportSuccess(true)
				window.setTimeout(() => {
					setIsExportSuccess(false)
				}, 1000)
			} catch (error) {
				alert(getErrorMessage(error, 'Failed to export selected bulk wines. Please try again.'))
			} finally {
				setIsExporting(false)
			}
		}
	}

	return (
		<>
			<EntityListLayout
				title="Bulk Wine"
				addButtonLabel="Add New Bulk Wine"
				onAdd={() => navigate('/bulk-wines/new')}
				entriesCount={totalEntries}
				onOpenFilter={() => setIsFilterOpen(true)}
				currentPageSize={itemsPerPage}
				onPageSizeChange={handlePageSizeChange}
				toolbarActions={
					isSuperadmin ? (
						<div className="flex items-center gap-3">
							<button
								type="button"
								onClick={() => {
									void handleExportSelected()
								}}
								disabled={selectedCount === 0 || isExporting || isExportSuccess}
								aria-busy={isExporting || isExportSuccess}
								className="flex items-center gap-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
								style={{
									height: '38px',
									borderRadius: '6px',
									backgroundColor: '#F6F5F3',
									border: '1px solid #D0D5DD',
									padding: '0 16px',
								}}
							>
								<Download size={20} color="#404040" aria-hidden="true" />
								<span
									style={{
										fontFamily: 'Oxygen',
										fontSize: '14px',
										fontWeight: 400,
										color: '#344054',
									}}
								>
									{isExporting ? `Exporting (${selectedCount})...` : `Export(${selectedCount})`}
								</span>
							</button>
							<EmailExportCheckbox checked={emailMode} onChange={setEmailMode} />
						</div>
					) : undefined
				}
			>
				<div className="relative">
					<LoadingOverlay visible={isFetching && bulkWines.length > 0} message="Loading..." />
					<Card noPadding>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHeaderCell style={{ width: columnWidths.select }}>
										<div className="flex justify-center">
											<SelectionCheckbox
												checked={allVisibleSelected}
												onChange={handleToggleAllVisibleWines}
												ariaLabel="Select all visible bulk wines"
											/>
										</div>
									</TableHeaderCell>
									<TableHeaderCell
										onClick={() => handleSort('code')}
										style={{ width: columnWidths.code, cursor: 'pointer' }}
									>
										<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0' }}>
											<SortIcon field="code" />
											<span>Code</span>
										</div>
									</TableHeaderCell>
									<TableHeaderCell
										onClick={() => handleSort('variety')}
										style={{ width: columnWidths.variety, cursor: 'pointer' }}
									>
										<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0' }}>
											<SortIcon field="variety" />
											<span>Variety</span>
										</div>
									</TableHeaderCell>
									<TableHeaderCell
										onClick={() => handleSort('vintage')}
										style={{ width: columnWidths.vintage, cursor: 'pointer' }}
									>
										<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0' }}>
											<SortIcon field="vintage" />
											<span>Vintage</span>
										</div>
									</TableHeaderCell>
									<TableHeaderCell
										onClick={() => handleSort('volume')}
										style={{ width: columnWidths.volume, cursor: 'pointer' }}
									>
										<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0' }}>
											<SortIcon field="volume" />
											<span>Volume (L)</span>
										</div>
									</TableHeaderCell>
									<TableHeaderCell
										onClick={() => handleSort('countryRegion')}
										style={{ width: columnWidths.countryRegion, cursor: 'pointer' }}
									>
										<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0' }}>
											<SortIcon field="countryRegion" />
											<span>Country/ Region</span>
										</div>
									</TableHeaderCell>
									<TableHeaderCell
										onClick={() => handleSort('location')}
										style={{ width: columnWidths.location, cursor: 'pointer' }}
									>
										<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0' }}>
											<SortIcon field="location" />
											<span>Location</span>
										</div>
									</TableHeaderCell>
									<TableHeaderCell
										onClick={() => handleSort('composition')}
										style={{ width: columnWidths.composition, cursor: 'pointer' }}
									>
										<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0' }}>
											<SortIcon field="composition" />
											<span>Composition</span>
										</div>
									</TableHeaderCell>
									<TableHeaderCell
										onClick={() => handleSort('pricePerLiter')}
										style={{ width: columnWidths.price, cursor: 'pointer' }}
									>
										<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0' }}>
											<SortIcon field="pricePerLiter" />
											<span>Price</span>
										</div>
									</TableHeaderCell>
									<TableHeaderCell
										onClick={() => handleSort('supplierName')}
										style={{ width: columnWidths.supplier, cursor: 'pointer' }}
									>
										<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0' }}>
											<SortIcon field="supplierName" />
											<span>Supplier</span>
										</div>
									</TableHeaderCell>
									<TableHeaderCell style={{ width: columnWidths.actions, textAlign: 'center' }}>
										Actions
									</TableHeaderCell>
								</TableRow>
							</TableHeader>
							<TableBody>
								{renderState.status === 'loading' ? (
									<TableRow>
										<TableCell colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
											Loading bulk wines...
										</TableCell>
									</TableRow>
								) : renderState.status === 'error' ? (
									<TableRow>
										<TableCell colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
											<div className="text-red-600 mb-2 font-semibold">Failed to load bulk wines</div>
											<div className="text-sm text-neutral-600 mb-4">
												{renderState.error instanceof Error
													? renderState.error.message.includes('ERR_CONNECTION_REFUSED')
														? 'Cannot connect to API server. Please ensure the API is running.'
														: renderState.error.message
													: 'An unexpected error occurred'}
											</div>
											<button
												onClick={() => window.location.reload()}
												className="px-4 py-2 bg-neutral-900 text-white rounded text-sm hover:bg-neutral-800"
											>
												Reload
											</button>
										</TableCell>
									</TableRow>
								) : renderState.status === 'empty' ? (
									<TableRow>
										<TableCell colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
											No bulk wines found
										</TableCell>
									</TableRow>
								) : (
									sortedBulkWines.map((wine) => (
										<TableRow key={wine.id} onClick={() => navigate(`/bulk-wines/${wine.id}`)}>
											<TableCell>
												<div
													className="flex justify-center"
													onClick={(e) => {
														e.stopPropagation()
													}}
												>
													<SelectionCheckbox
														checked={selectedWineIds.includes(wine.id)}
														onChange={() => handleToggleWineSelection(wine.id)}
														ariaLabel={`Select bulk wine ${wine.code}`}
													/>
												</div>
											</TableCell>
											<TableCell style={{ wordBreak: 'break-word' }}>{wine.code}</TableCell>
											<TableCell style={{ wordBreak: 'break-word' }}>{wine.variety}</TableCell>
											<TableCell>{wine.vintage}</TableCell>
											<TableCell>{formatVolume(wine.volume)}</TableCell>
											<TableCell>
												<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
													{wine.countryRegion.includes('/') ? (
														<>
															<span>{wine.countryRegion.split('/')[0].trim()}</span>
															<span style={{ color: '#667085', fontSize: '11px' }}>
																{wine.countryRegion.split('/')[1].trim()}
															</span>
														</>
													) : (
														<span>{wine.countryRegion}</span>
													)}
												</div>
											</TableCell>
											<TableCell style={{ wordBreak: 'break-word' }}>{wine.location}</TableCell>
											<TableCell>
												<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
													{wine.composition.split(',').map((variety, idx) => (
														<span key={idx} style={{ fontSize: '12px', color: '#101828', lineHeight: '16px' }}>
															{variety.trim()}
														</span>
													))}
												</div>
											</TableCell>
											<TableCell style={{ whiteSpace: 'nowrap' }}>
												{wine.pricePerLiter !== null && wine.pricePerLiter !== undefined
													? `${wine.priceCurrency || 'NZD'} ${parseFloat(wine.pricePerLiter.toString()).toFixed(2)}/L`
													: 'N/A'}
											</TableCell>
											<TableCell style={{ wordBreak: 'break-word', lineHeight: '16px' }}>{wine.supplierName}</TableCell>
											<TableCell>
												<div className="flex items-center justify-center">
													<ActionMenu
														ariaLabel={`Actions for ${wine.code}`}
														items={[
															{
																label: 'View',
																icon: <Eye size={16} />,
																onClick: () => navigate(`/bulk-wines/${wine.id}`),
															},
															{
																label: 'Edit',
																icon: <Pencil size={16} />,
																onClick: () => navigate(`/bulk-wines/${wine.id}/edit`),
															},
															{
																label: 'Delete',
																icon: <Trash2 size={16} />,
																destructive: true,
																onClick: () => void handleDeleteBulkWine(wine),
															},
														]}
													/>
												</div>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</Card>
				</div>

				{/* Pagination Controls */}
				<Pagination
					currentPage={currentPage}
					totalPages={totalPages}
					totalEntries={totalEntries}
					pageSize={typeof itemsPerPage === 'number' ? itemsPerPage : totalEntries}
					onPageChange={setCurrentPage}
					loading={isFetching}
				/>

				<FilterPanel
					isOpen={isFilterOpen}
					onClose={() => setIsFilterOpen(false)}
					onFilter={() => {
						setTimeout(() => {
							if (getFiltersRef) {
								const filters = getFiltersRef()
								setActiveFilters(filters)
								setCurrentFilters(filters)
								setCurrentPage(1) // Reset to first page when filtering
							}
							setIsFilterOpen(false)
						}, 300)
					}}
					onClear={() => {
						setTimeout(() => {
							setActiveFilters(null)
							setCurrentFilters(null)
							setCurrentPage(1) // Reset to first page when clearing
							setIsFilterOpen(false)
						}, 300)
					}}
				>
					<BulkWineFilters
						key={currentFilters ? 'filtered' : 'cleared'}
						initialValues={currentFilters || undefined}
						onFiltersReady={handleFiltersReady}
					/>
				</FilterPanel>
			</EntityListLayout>

			<ExportOverlay
				isExporting={isExporting}
				isSuccess={isExportSuccess}
				loadingMessage="Preparing ZIP export..."
				successMessage="Export complete"
			/>

			<SendEmailModal
				isOpen={emailExport.isModalOpen}
				onClose={emailExport.closeModal}
				onSend={emailExport.sendEmail}
				defaultSubject={emailExport.modalData?.subject || ''}
				defaultBody={emailExport.modalData?.body || ''}
				attachmentNames={emailExport.modalData?.attachmentNames || []}
				defaultReplyTo={currentUser?.email}
			/>
		</>
	)
}

export default BulkWinePage
